import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  ShoppingBag, 
  Users, 
  TrendingUp, 
  Settings, 
  LogOut, 
  Search, 
  Bell,
  Plus,
  ArrowUpRight,
  MoreVertical,
  Layers,
  ChevronRight,
  Shield,
  User,
  Activity,
  Server,
  Lock,
  Globe,
  Database,
  CheckCircle2,
  Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { shopService } from '../../services/shopService';
import { userService } from '../../services/userService';
import { authService } from '../../services/authService';
import { showSuccess, showError } from '../../utils/alert';

const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [shops, setShops] = useState([]);
  const [users, setUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [stats, setStats] = useState({
    totalShops: 0,
    totalUsers: 0,
    totalScratches: 0,
    pendingInquiries: 0
  });
  const [globalSettings, setGlobalSettings] = useState({
    platformName: 'Digi Sahyadri Elite',
    starterPrice: '999',
    premiumPrice: '2499',
    enterprisePrice: '4999'
  });
  const [loading, setLoading] = useState(true);
  const [selectedShopForCreds, setSelectedShopForCreds] = useState(null);
  const [selectedShopForEdit, setSelectedShopForEdit] = useState(null);
  const [userMetrics, setUserMetrics] = useState({});
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log("Fetching platform data...");
      const [allShops, allUsers, userCount, scratchCount, allInquiries, settings, metrics] = await Promise.all([
        shopService.getAllShops(),
        userService.getAllUsers(),
        userService.getTotalUsers(),
        userService.getTotalScratches(),
        shopService.getAllInquiries(),
        shopService.getGlobalSettings(),
        userService.getUniqueUsersPerShop()
      ]);

      console.log("Data Received:", { shops: allShops?.length, users: userCount, scratches: scratchCount, metrics });

      if (settings) setGlobalSettings(settings);
      if (metrics) setUserMetrics(metrics);

      setShops(allShops || []);
      setUsers(allUsers || []);
      setInquiries(allInquiries || []);
      setStats({
        totalShops: allShops ? allShops.length : 0,
        totalUsers: userCount || 0,
        totalScratches: scratchCount || 0,
        pendingInquiries: allInquiries ? allInquiries.filter(i => i.status === 'pending').length : 0
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDeleteShop = async (shopId) => {
    try {
      await shopService.deleteShop(shopId);
      showSuccess('Terminated', 'Merchant node has been permanently removed.');
      fetchData(); // Refresh data
    } catch (error) {
      showError('Action Failed', 'Unable to terminate node.');
    }
  };

  const handleUpdateShop = async (updatedData) => {
    try {
      setLoading(true);
      await shopService.updateShop(selectedShopForEdit.id, updatedData);
      showSuccess('Success', 'Profile updated successfully');
      setSelectedShopForEdit(null);
      fetchData();
    } catch (error) {
      showError('Update Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (settingsData) => {
    try {
      setLoading(true);
      await shopService.updateGlobalSettings(settingsData);
      showSuccess('Success', 'System configuration updated');
      setGlobalSettings(settingsData);
    } catch (error) {
      showError('Update Failed', error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate('/login');
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const menuItems = [
    { id: 'dashboard', name: 'Analytics Hub', icon: <LayoutDashboard size={20} /> },
    { id: 'merchants', name: 'Merchant Registry', icon: <ShoppingBag size={20} /> },
    { id: 'inquiries', name: 'Merchant Leads', icon: <Bell size={20} /> },
    { id: 'users', name: 'User Directory', icon: <Users size={20} /> },
    { id: 'reports', name: 'Performance', icon: <TrendingUp size={20} /> },
    { id: 'settings', name: 'System Settings', icon: <Settings size={20} /> },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardView stats={stats} shops={shops} loading={loading} onShowCreds={setSelectedShopForCreds} onEditShop={setSelectedShopForEdit} onDeleteShop={handleDeleteShop} />;
      case 'merchants': return <MerchantsView shops={shops} loading={loading} onShowCreds={setSelectedShopForCreds} onEditShop={setSelectedShopForEdit} onDeleteShop={handleDeleteShop} />;
      case 'inquiries': return <InquiriesView inquiries={inquiries} loading={loading} />;
      case 'users': return <UsersView shops={shops} loading={loading} userMetrics={userMetrics} />;
      case 'reports': return <ReportsView shops={shops} users={users} />;
      case 'settings': return <SettingsView settings={globalSettings} onSave={handleUpdateSettings} />;
      default: return <DashboardView stats={stats} shops={shops} loading={loading} onShowCreds={setSelectedShopForCreds} onEditShop={setSelectedShopForEdit} onDeleteShop={handleDeleteShop} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#fdfdfd] flex text-[#1a1a1a] relative">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
      {/* Sidebar */}
      <aside className={`bg-white border-r border-black/[0.05] transition-all duration-500 flex flex-col shrink-0 ${isSidebarOpen ? 'w-72' : 'w-24'} z-50 sticky top-0 h-screen`}>
        <div className="p-8 flex items-center gap-4 cursor-pointer" onClick={() => setSidebarOpen(!isSidebarOpen)}>
          {isSidebarOpen && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
              <img src="/Sahyadri Tech Logo.png2.png" alt="Sahyadri Tech" className="h-8 w-auto object-contain" />
            </motion.div>
          )}
        </div>

        <nav className="flex-1 px-4 py-8 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-300 group ${
                activeTab === item.id 
                ? 'bg-black text-[#d4af37] shadow-xl' 
                : 'text-[#ccc] hover:text-black hover:bg-black/5'
              }`}
            >
              <span className={`shrink-0 transition-transform group-hover:scale-110`}>
                {item.icon}
              </span>
              {isSidebarOpen && <span className="text-[11px] font-bold uppercase tracking-[0.2em]">{item.name}</span>}
              {activeTab === item.id && isSidebarOpen && <motion.div layoutId="active" className="ml-auto w-1.5 h-1.5 bg-[#d4af37] rounded-full shadow-[0_0_10px_#d4af37]" />}
            </button>
          ))}
        </nav>

        <div className="p-6 border-t border-black/[0.05]">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-4 py-4 text-rose-500 bg-rose-500/5 rounded-2xl hover:bg-rose-500/10 transition-all group"
          >
            <LogOut size={20} className="group-hover:-translate-x-1 transition-transform" />
            {isSidebarOpen && <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Exit System</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 min-h-screen overflow-y-auto custom-scrollbar relative px-8 py-8 relative z-10">
        {/* Header */}
        <header className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-6">
            <div>
              <h1 className="text-3xl font-serif text-black tracking-tight capitalize">{activeTab.replace('-', ' ')}</h1>
              <p className="text-[#666] text-[10px] font-bold uppercase tracking-[0.3em] mt-2">Core System Surveillance & Governance</p>
            </div>
            <button 
              onClick={() => {
                setLoading(true);
                window.location.reload(); 
              }}
              className="p-3 bg-black/5 rounded-2xl text-black hover:bg-black hover:text-[#d4af37] transition-all"
              title="Sync Platform Data"
            >
              <Activity size={18} />
            </button>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 bg-white border border-black/[0.05] p-1.5 rounded-2xl shadow-sm">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#ccc] group-focus-within:text-black transition-colors" size={16} />
                <input 
                  type="text" 
                  placeholder="Global search..." 
                  className="bg-black/[0.02] rounded-xl py-2.5 pl-12 pr-6 text-xs text-black focus:outline-none w-48 transition-all focus:w-72 border border-transparent focus:border-black/10"
                />
              </div>
              <button className="p-2.5 text-[#ccc] hover:text-black transition-all relative">
                <Bell size={20} />
                <span className="absolute top-2 right-2 w-2 h-2 bg-black rounded-full" />
              </button>
            </div>

            <button 
              onClick={() => navigate('/super-admin/create-shop')}
              className="luxury-button !py-4 flex items-center gap-3"
            >
              <Plus size={16} />
              Onboard Merchant
            </button>
          </div>
        </header>

        {/* Content Section */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Credential Modal */}
      <AnimatePresence>
        {selectedShopForCreds && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setSelectedShopForCreds(null)}
              className="absolute inset-0 bg-white/80 backdrop-blur-md" 
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="luxury-card w-full max-w-md p-10 relative z-10"
            >
              <div className="text-center mb-10">
                <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center text-[#d4af37] mx-auto mb-6 shadow-xl">
                  <Shield size={32} />
                </div>
                <h3 className="text-2xl font-serif text-black tracking-tight">Node Credentials</h3>
                <p className="text-[#666] text-[10px] font-bold uppercase tracking-widest mt-2">Access Key for {selectedShopForCreds.shopName}</p>
              </div>

              <div className="space-y-6">
                <div className="p-4 bg-black/[0.02] rounded-2xl border border-black/[0.05]">
                  <p className="text-[9px] font-bold text-[#999] uppercase tracking-widest mb-1">Identity (Email)</p>
                  <p className="text-sm text-black font-medium select-all">{selectedShopForCreds.email}</p>
                </div>
                <div className="p-4 bg-black/[0.02] rounded-2xl border border-black/[0.05]">
                  <p className="text-[9px] font-bold text-[#999] uppercase tracking-widest mb-1">Access Key (Password)</p>
                  <p className="text-sm text-black font-bold tracking-widest select-all">{selectedShopForCreds.password}</p>
                </div>
              </div>

              <button 
                onClick={() => setSelectedShopForCreds(null)}
                className="w-full mt-10 luxury-button !py-4"
              >
                Close Security Vault
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <EditModal 
        isOpen={!!selectedShopForEdit} 
        shop={selectedShopForEdit} 
        onClose={() => setSelectedShopForEdit(null)} 
        onSave={handleUpdateShop} 
      />
    </div>
  );
};

/* --- SUB-VIEWS --- */

const DashboardView = ({ stats, shops, loading, onShowCreds, onEditShop, onDeleteShop }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      <StatCard title="Global Merchants" value={stats.totalShops} icon={<ShoppingBag size={24} />} trend="+12% from last month" color="bg-white" iconColor="text-black" />
      <StatCard title="Total Userbase" value={stats.totalUsers} icon={<Users size={24} />} trend="+8.4k new signups" color="bg-white" iconColor="text-black" />
      <StatCard title="Platform Scratches" value={stats.totalScratches} icon={<TrendingUp size={24} />} trend="Live platform stats" color="bg-white" iconColor="text-black" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-12 luxury-card p-8 border-black/[0.05]">
        <h3 className="text-xl font-serif text-black tracking-tight mb-8">Top 5 Enterprise Registry</h3>
        <MerchantTable shops={shops.slice(0, 5)} loading={loading} onShowCreds={onShowCreds} onEditShop={onEditShop} onDeleteShop={onDeleteShop} />
      </div>

    </div>
  </div>
);

const MerchantsView = ({ shops, loading, onShowCreds, onEditShop, onDeleteShop }) => (
  <div className="luxury-card p-8 border-black/[0.05]">
    <div className="flex justify-between items-center mb-10">
      <h3 className="text-xl font-serif text-black tracking-tight">Complete Merchant Directory</h3>
      <div className="flex gap-4">
        <button className="px-6 py-2 bg-black/5 rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-[#d4af37] transition-all">Filter</button>
        <button className="px-6 py-2 bg-black text-[#d4af37] rounded-xl text-[10px] font-bold uppercase tracking-widest shadow-lg">Export CSV</button>
      </div>
    </div>
    <MerchantTable shops={shops} loading={loading} onShowCreds={onShowCreds} onEditShop={onEditShop} onDeleteShop={onDeleteShop} />
  </div>
);

const InquiriesView = ({ inquiries, loading }) => (
  <div className="luxury-card p-8 border-black/[0.05]">
    <div className="flex justify-between items-center mb-10">
      <h3 className="text-xl font-serif text-black tracking-tight">Merchant Inquiries</h3>
      <div className="bg-black text-[#d4af37] px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest">
        {inquiries.length} New Leads
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="text-[9px] font-bold text-[#999] uppercase tracking-[0.2em] border-b border-black/[0.05]">
            <th className="pb-6">Merchant Detail</th>
            <th className="pb-6">Business Type</th>
            <th className="pb-6">Contact Info</th>
            <th className="pb-6">Message</th>
            <th className="pb-6">Received</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-black/[0.05]">
          {loading ? (
            <tr><td colSpan="5" className="py-20 text-center text-[#999] text-xs uppercase tracking-widest">Accessing inquiry datastreams...</td></tr>
          ) : inquiries.length === 0 ? (
            <tr><td colSpan="5" className="py-20 text-center text-[#999] text-xs uppercase tracking-widest">No inquiries found</td></tr>
          ) : (
            inquiries.map((item, i) => (
              <tr key={item.id} className="group hover:bg-black/[0.02] transition-colors">
                <td className="py-6">
                  <div>
                    <p className="text-sm font-semibold text-black">{item.ownerName}</p>
                    <p className="text-[10px] text-[#999] font-bold uppercase tracking-tighter mt-1">{item.shopName}</p>
                  </div>
                </td>
                <td className="py-6">
                  <span className="px-3 py-1 bg-black/5 rounded-full text-[10px] font-bold text-black uppercase tracking-widest">{item.businessType || 'General'}</span>
                </td>
                <td className="py-6">
                  <p className="text-sm font-bold text-black">{item.phoneNumber}</p>
                </td>
                <td className="py-6 max-w-xs">
                  <p className="text-xs text-[#666] line-clamp-2">{item.message || 'No message provided'}</p>
                </td>
                <td className="py-6 text-[10px] font-bold text-[#999] uppercase">
                  {item.createdAt?.toDate().toLocaleDateString() || 'Recently'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const UsersView = ({ shops, loading, userMetrics }) => (
  <div className="luxury-card p-8 border-black/[0.05]">
    <h3 className="text-xl font-serif text-black tracking-tight mb-8">Platform Users by Merchant</h3>
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="text-[9px] font-bold text-[#999] uppercase tracking-[0.2em] border-b border-black/[0.05]">
            <th className="pb-6">Merchant Identity</th>
            <th className="pb-6">Enterprise</th>
            <th className="pb-6">Total Userbase</th>
            <th className="pb-6">User Velocity</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-black/[0.05]">
          {loading ? (
            <tr><td colSpan="4" className="py-20 text-center text-[#999] text-xs uppercase tracking-widest">Accessing user metrics...</td></tr>
          ) : shops.length === 0 ? (
            <tr><td colSpan="4" className="py-20 text-center text-[#999] text-xs uppercase tracking-widest">No merchant nodes detected</td></tr>
          ) : (
            shops.map((shop, i) => (
              <tr key={shop.id} className="group hover:bg-black/[0.02] transition-colors">
                <td className="py-6 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-black/5 flex items-center justify-center text-black group-hover:bg-black group-hover:text-[#d4af37] transition-all">
                    <User size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-black">{shop.ownerName}</p>
                    <p className="text-[10px] text-[#999] font-bold uppercase tracking-tighter">Owner Identity</p>
                  </div>
                </td>
                <td className="py-6">
                  <p className="text-xs font-bold text-black uppercase tracking-widest">{shop.shopName}</p>
                </td>
                <td className="py-6">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-black">{userMetrics[shop.id] || 0}</span>
                    <span className="text-[10px] text-[#999] font-medium uppercase tracking-tighter">Customers</span>
                  </div>
                </td>
                <td className="py-6">
                  <div className="w-24 h-1.5 bg-black/[0.02] rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }} 
                      animate={{ width: `${Math.min(((userMetrics[shop.id] || 0) / 100) * 100, 100)}%` }} 
                      className="h-full bg-emerald-500/40 rounded-full" 
                    />
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const ReportsView = ({ shops, users }) => {
  // Calculate Growth (Shops per month for last 6 months)
  const getGrowthData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentMonth = new Date().getMonth();
    const last6Months = [];
    
    for (let i = 5; i >= 0; i--) {
      const m = (currentMonth - i + 12) % 12;
      last6Months.push({ name: months[m], count: 0 });
    }

    shops.forEach(shop => {
      if (shop.createdAt) {
        const date = shop.createdAt.toDate();
        const monthName = months[date.getMonth()];
        const entry = last6Months.find(m => m.name === monthName);
        if (entry) entry.count++;
      }
    });

    return last6Months;
  };

  // Calculate Distribution by Business Type
  const getIndustryData = () => {
    const industries = {};
    shops.forEach(shop => {
      const type = shop.businessType || 'General';
      industries[type] = (industries[type] || 0) + 1;
    });

    return Object.entries(industries)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  };

  const growthData = getGrowthData();
  const industryData = getIndustryData();
  const maxGrowth = Math.max(...growthData.map(d => d.count), 1);
  const maxIndustry = Math.max(...industryData.map(d => d.value), 1);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      <div className="luxury-card p-8 border-black/[0.05]">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-serif text-black tracking-tight">Merchant Growth</h3>
          <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest bg-emerald-500/5 px-3 py-1 rounded-full border border-emerald-500/10 flex items-center gap-2">
            <TrendingUp size={10} /> +{((growthData[5].count / (growthData[4].count || 1)) * 100 - 100).toFixed(0)}%
          </span>
        </div>
        <div className="h-64 flex items-end gap-4 justify-between px-2">
          {growthData.map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-4 group">
              <div className="w-full bg-black/[0.02] rounded-2xl relative h-48 overflow-hidden">
                <motion.div 
                  initial={{ height: 0 }} 
                  animate={{ height: `${(d.count / maxGrowth) * 100}%` }} 
                  className="absolute bottom-0 left-0 right-0 bg-black/5 group-hover:bg-[#d4af37]/40 transition-all flex items-start justify-center pt-2"
                >
                  {d.count > 0 && <span className="text-[10px] font-bold text-black opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</span>}
                </motion.div>
              </div>
              <span className="text-[10px] font-bold text-[#999] uppercase tracking-widest">{d.name}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="luxury-card p-8 border-black/[0.05]">
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-xl font-serif text-black tracking-tight">Industry Distribution</h3>
          <span className="text-[10px] font-bold text-[#999] uppercase tracking-widest">Top Sectors</span>
        </div>
        <div className="space-y-6">
          {industryData.length > 0 ? industryData.map((d, i) => (
            <div key={i} className="space-y-2">
              <div className="flex justify-between items-center px-1">
                <span className="text-[10px] font-bold text-black uppercase tracking-widest">{d.name}</span>
                <span className="text-[10px] font-bold text-[#999]">{d.value} Nodes</span>
              </div>
              <div className="h-3 w-full bg-black/[0.02] rounded-full overflow-hidden">
                <motion.div 
                  initial={{ width: 0 }} 
                  animate={{ width: `${(d.value / maxIndustry) * 100}%` }} 
                  className="h-full bg-black/5 hover:bg-[#d4af37]/40 transition-all rounded-full" 
                />
              </div>
            </div>
          )) : (
            <div className="h-64 flex items-center justify-center text-[#ccc] text-[10px] font-bold uppercase tracking-widest">
              No industry data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



const SettingsView = ({ settings, onSave }) => {
  const [formData, setFormData] = useState(settings);

  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  return (
    <div className="max-w-2xl luxury-card p-8 border-black/[0.05]">
      <h3 className="text-xl font-serif text-black tracking-tight mb-8">System Configuration</h3>
      <div className="space-y-8">
        <div className="space-y-4">
          <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Platform Name</label>
          <input 
            type="text" 
            value={formData.platformName} 
            onChange={(e) => setFormData({ ...formData, platformName: e.target.value })}
            className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10" 
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-4">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Starter Price (₹)</label>
                <input 
                    type="number" 
                    value={formData.starterPrice} 
                    onChange={(e) => setFormData({ ...formData, starterPrice: e.target.value })}
                    className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10" 
                />
            </div>
            <div className="space-y-4">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Premium Price (₹)</label>
                <input 
                    type="number" 
                    value={formData.premiumPrice} 
                    onChange={(e) => setFormData({ ...formData, premiumPrice: e.target.value })}
                    className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10" 
                />
            </div>
            <div className="space-y-4">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Enterprise Price (₹)</label>
                <input 
                    type="number" 
                    value={formData.enterprisePrice} 
                    onChange={(e) => setFormData({ ...formData, enterprisePrice: e.target.value })}
                    className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10" 
                />
            </div>
        </div>

        <button 
            onClick={() => onSave(formData)}
            className="luxury-button w-full !py-4"
        >
            Update Global Config
        </button>
      </div>
    </div>
  );
};

/* --- COMPONENTS --- */

const StatCard = ({ title, value, icon, trend, color, iconColor }) => (
  <div className={`luxury-card p-8 ${color} hover:border-black/10 group transition-all border-black/[0.05]`}>
    <div className="flex justify-between items-start mb-8">
      <div className={`w-14 h-14 rounded-3xl bg-black/5 flex items-center justify-center ${iconColor} shadow-sm group-hover:bg-black group-hover:text-[#d4af37] transition-all`}>
        {icon}
      </div>
      <div className="bg-emerald-500/5 text-emerald-600 px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest flex items-center gap-1 border border-emerald-500/10">
        <ArrowUpRight size={10} />
        Active
      </div>
    </div>
    <div>
      <p className="text-[#999] text-[10px] font-bold uppercase tracking-widest mb-1">{title}</p>
      <h2 className="text-4xl font-serif text-black tracking-tight">{value}</h2>
      <p className="text-[#ccc] text-[9px] font-bold mt-4 uppercase tracking-[0.2em]">{trend}</p>
    </div>
  </div>
);

const MerchantTable = ({ shops, loading, onShowCreds, onEditShop, onDeleteShop }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left">
      <thead>
        <tr className="text-[9px] font-bold text-[#999] uppercase tracking-[0.2em] border-b border-black/[0.05]">
          <th className="pb-6">Enterprise</th>
          <th className="pb-6">Governance Plan</th>
          <th className="pb-6">Connectivity</th>
          <th className="pb-6">Vector Score</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-black/[0.05]">
        {loading ? (
          <tr><td colSpan="4" className="py-20 text-center text-[#999] text-xs">Accessing datastreams...</td></tr>
        ) : shops.length === 0 ? (
          <tr><td colSpan="4" className="py-20 text-center text-[#999] text-xs uppercase tracking-widest">No active merchant nodes detected</td></tr>
        ) : (
          shops.map((shop, i) => (
            <tr key={shop.id} className="group hover:bg-black/[0.02] transition-colors">
              <td className="py-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-black/5 border border-black/[0.05] flex items-center justify-center text-black group-hover:bg-black group-hover:text-[#d4af37] transition-all">
                    <ShoppingBag size={20} />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold text-black text-sm tracking-tight">{shop.shopName}</span>
                    <span className="text-[10px] text-[#999] font-bold uppercase tracking-tighter mt-0.5">{shop.ownerName}</span>
                  </div>
                </div>
              </td>
              <td className="py-6">
                <div className="flex items-center gap-2">
                  <Shield size={12} className="text-black/20" />
                  <span className="text-xs text-[#666] font-medium tracking-wide">{shop.plan} Architecture</span>
                </div>
              </td>
              <td className="py-6">
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full ${shop.status === 'active' ? 'bg-emerald-600 shadow-[0_0_10px_#10b981]' : 'bg-rose-600'}`} />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#999]">{shop.status || 'Linked'}</span>
                </div>
              </td>
              <td className="py-6">
                <div className="flex items-center justify-between">
                  <div className="relative group/menu">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      className="p-2 text-[#ccc] hover:text-black transition-all"
                    >
                      <MoreVertical size={16} />
                    </button>
                    
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-black/[0.05] rounded-2xl shadow-2xl opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all z-50 overflow-hidden">
                      <button 
                        onClick={() => onShowCreds(shop)}
                        className="w-full text-left px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-[#999] hover:bg-black hover:text-[#d4af37] transition-all flex items-center gap-3"
                      >
                        <Lock size={14} /> Access Vault
                      </button>
                      <button 
                        onClick={() => onEditShop(shop)}
                        className="w-full text-left px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-[#999] hover:bg-black hover:text-[#d4af37] transition-all flex items-center gap-3 border-y border-black/[0.03]"
                      >
                        <User size={14} /> Edit Profile
                      </button>
                      <button 
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to terminate ${shop.shopName}?`)) {
                            onDeleteShop(shop.id);
                          }
                        }}
                        className="w-full text-left px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-rose-500 hover:bg-rose-500 hover:text-white transition-all flex items-center gap-3"
                      >
                        <Trash2 size={14} /> Terminate
                      </button>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const EditModal = ({ isOpen, shop, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    shopName: '',
    ownerName: '',
    plan: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    if (shop) {
      setFormData({
        shopName: shop.shopName || '',
        ownerName: shop.ownerName || '',
        plan: shop.plan || 'STARTER',
        startDate: shop.startDate || '',
        endDate: shop.endDate || ''
      });
    }
  }, [shop]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-white/80 backdrop-blur-md" 
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="luxury-card w-full max-w-xl p-10 relative z-10"
        >
          <div className="mb-10">
            <h3 className="text-2xl font-serif text-black tracking-tight">Edit Merchant Profile</h3>
            <p className="text-[#666] text-[10px] font-bold uppercase tracking-widest mt-2">Modify identity & governance for this node</p>
          </div>

          <div className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Enterprise Name</label>
                <input 
                  type="text" 
                  value={formData.shopName}
                  onChange={(e) => setFormData({ ...formData, shopName: e.target.value })}
                  className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Owner Identity</label>
                <input 
                  type="text" 
                  value={formData.ownerName}
                  onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                  className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Subscription Start Date</label>
                <input 
                  type="date" 
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Subscription End Date</label>
                <input 
                  type="date" 
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/10"
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Governance Plan</label>
              <div className="grid grid-cols-3 gap-4">
                {['STARTER', 'PREMIUM', 'ENTERPRISE'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setFormData({ ...formData, plan: p })}
                    className={`py-3 px-4 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                      formData.plan === p 
                      ? 'bg-black text-[#d4af37] border-black shadow-lg' 
                      : 'bg-black/[0.02] border-black/[0.05] text-[#ccc] hover:border-black/20'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button onClick={onClose} className="flex-1 py-4 rounded-2xl border border-black/[0.05] text-[10px] font-black uppercase tracking-widest text-[#999] hover:bg-black/5 transition-all">
                Cancel
              </button>
              <button 
                onClick={() => onSave(formData)}
                className="flex-1 luxury-button !py-4"
              >
                Save Changes
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};







export default SuperAdminDashboard;
